"use client";
import { CustomInput } from "@shared/src/components/common/form-components/CustomInput";
import { getSocialLogins, SocialWalletConfig } from "@shared/src/config/wallet-config";
import { MdEmail } from "react-icons/md";
import { FaArrowRight } from "react-icons/fa6";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuthStore } from "@shared/src/stores/useAuthStore";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@shared/src/components/ui/form";
import { Spinner } from "@shared/src/components/common/misc/Spinner";
import { useState } from "react";
import { useChainId } from "wagmi";
import useAuth from "@shared/src/hooks/auth/useAuth";
import { ixToast } from "@shared/src/utils/ixToast";
import { AwaitingConfirmation } from "@shared/src/components/modules/auth/WalletConnection";
import { Button } from "@shared/src/components/ui/button";

const schema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type FormValues = z.infer<typeof schema>;
let selectedSocial: SocialWalletConfig | null = null;

export function SocialLoginView() {
  const socialLogins = getSocialLogins();
  const { setEmail, setIsVerifying, setShowBackButton, walletStatus, setWalletStatus, goBack } = useAuthStore();
  const { authenticate } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const chainId = useChainId();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormValues) => {
    setIsLoading(true);
    try {
      setEmail(data.email);
      await authenticate({
        type: "email",
        email: data.email,
        emailMode: "otp",
        connectorId: "alchemy",
      });
      setIsVerifying(true);
      setShowBackButton(true);
    } catch (error: any) {
      ixToast.error(error.message || "Failed to send verification code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (social: SocialWalletConfig) => {
    selectedSocial = social;
    setWalletStatus("awaiting_confirmation", social.name);
    try {
      await authenticate({
        type: "oauth",
        authProviderId: social.id as "google" | "facebook" | "apple",
        mode: "popup",
        connectorIcon: social.logoUrl,
        connectorId: "alchemy",
      });
      setWalletStatus("success", social.name);
    } catch (error: any) {
      console.log(error);
      setWalletStatus("error", social.name);
      ixToast.error(error.message || "Failed to authenticate");
    }
  };

  return walletStatus === "awaiting_confirmation" ? (
    <div className="flex flex-col items-center w-full min-h-[300px]">
      <div className="flex-1 flex flex-col items-center gap-4">
        <AwaitingConfirmation
          walletName={selectedSocial?.name || "Login"}
          walletIcon={selectedSocial?.logoUrl || ""}
          type="social"
        />
      </div>
    </div>
  ) : (
    <div className="flex flex-col md:gap-12 gap-8 items-center h-full w-full">
      <div className="flex gap-3">
        {socialLogins.map(social => (
          <button
            key={social.id}
            onClick={() => handleSocialLogin(social)}
            className="flex items-center justify-center p-2 rounded-xl border border-border-tertiary hover:opacity-75 transition-all duration-200"
          >
            <img src={social.logoUrl} alt={social.name} width={40} height={40} />
          </button>
        ))}
      </div>

      <div className="flex items-center gap-4 w-full px-4">
        <div className="h-[1px] flex-1 bg-border-tertiary" />
        <span className="text-text-secondary text-small">Or</span>
        <div className="h-[1px] flex-1 bg-border-tertiary" />
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <CustomInput
                    {...field}
                    placeholder="Email Address"
                    startIcon={<MdEmail size={20} className="text-icons-icon-brand" />}
                    endIcon={
                      <button type="submit" disabled={isLoading}>
                        {isLoading ? (
                          <Spinner className="text-text-primary" />
                        ) : (
                          <FaArrowRight size={20} className="cursor-pointer text-text-primary hover:opacity-75" />
                        )}
                      </button>
                    }
                    error={!!form.formState.errors.email}
                    className="text-text-primary"
                    containerClassName="bg-transparent"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </div>
  );
}
